import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Box,
  Button,
  Divider,
  Paper,
  Grid,
  Typography,
  useMediaQuery,
  Stack,
  alpha,
  Tabs,
  Tab,
} from "@mui/material";
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useTheme } from "@mui/material/styles";
import { areAssetsEqual } from "../../../../utils/hooks/dataHandler";
import useTabPersistence from "../../../../utils/hooks/useTabPersistence";
import ScheduleHeader from "../../../App/ScheduleHeader";
import ScheduleNotes from "../../ScheduleNotes";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PrincipalPaidDialog from "./PrincipalPaid";
import AssetInfoDialog from "./CreateEditAsset";
import PrincipalPaidTable from "./PrincipalPaidTable";
import AssetStatusPanel from "./AssetStatusPanel";
import AssetInfo from "./AssetInfo";
import {
  getCaKeys,
  getCaSettingByKey,
  getCaSummary,
} from "../../../../builders/settings/CaSetting";
import ReceiptIcon from "@mui/icons-material/Receipt";
import HirePurchaseSettingsDialog from "./HirePurchaseSettingsDialog";
import PdfGenerator from "../Pdf/HirePurchasePdf";
import HirePurchaseSummary from "./HirePurchaseSummary";
import AssetListView from "./AssetListView";
import DeleteAssetDialog from "./DeleteAssetDialog";
import { v4 as uuidv4 } from "uuid";
import FullscreenDialog from "../../../utils/dialog/FullscreenDialog";
import ViewModeToggle from "../CaSchedules/utils/ViewModeToggle";
import AssetImportManager from "./AssetImportManager";
import HirePurchaseExcelUpload from "./HirePurchaseExcelUpload";
import { HirePurchaseToolDialog } from "./HpTool";
import { useErrorDialog } from "../../../../contexts/ErrorDialogContext";
import { isEmptyObject } from "../../../../utils/helpers";

// Draggable Tab Component
const DraggableTab = ({ asset, index, isSelected, onSelect }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: asset.assetId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleClick = (e) => {
    // Only handle click if not dragging
    if (!isDragging) {
      onSelect(index);
    }
  };

  return (
    <Tab
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      label={asset.assetName}
      sx={{
        alignItems: "flex-start",
        textAlign: "left",
        minHeight: 48,
        fontWeight: isSelected ? 700 : 400,
        color: isSelected ? "primary.main" : "text.secondary",
        borderRadius: 2,
        fontSize: 15,
        px: 2,
        py: 1,
        bgcolor: isSelected
          ? (theme) => alpha(theme.palette.primary.main, 0.08)
          : "transparent",
        transition: "background 0.2s, color 0.2s",
        "&:hover": {
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
        },
        cursor: isDragging ? "grabbing" : "pointer",
        "&:active": {
          cursor: "grabbing",
        },
      }}
      onClick={handleClick}
    />
  );
};

// Add New Tab Component
const AddNewTab = ({ onAddNew }) => {
  return (
    <Tab
      icon={<AddCircleOutlineIcon color='primary' sx={{ fontSize: 22 }} />}
      aria-label='Add Asset'
      sx={{
        minHeight: 48,
        borderRadius: 2,
        px: 2,
        py: 1,
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
        "&:hover": {
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
        },
      }}
      onClick={onAddNew}
    />
  );
};

const processImportableAssets = (importItems, catMap, existingAssets) => {
  if (!importItems || importItems.length === 0 || isEmptyObject(importItems)) {
    return {
      allImportTypeAssets: [],
      importableAssets: [],
      importedAssets: [],
    };
  }

  // step 1: populate category info
  const populated = importItems.map((a) => ({
    ...a,
    category: catMap[a.categoryId] || a.category || null,
    source: a.source || "FA Additions",
  }));
  const deduped = [];
  const assetIndexMap = new Map();

  populated.forEach((asset) => {
    if (!asset.assetId) {
      deduped.push(asset);
      return;
    }

    const existingIndex = assetIndexMap.get(asset.assetId);
    if (existingIndex === undefined) {
      assetIndexMap.set(asset.assetId, deduped.length);
      deduped.push(asset);
      return;
    }

    if (
      deduped[existingIndex]?.source !== "FA Register" &&
      asset.source === "FA Register"
    ) {
      deduped[existingIndex] = asset;
    }
  });

  // step 2: check against existing assets
  const existingIds = new Set(existingAssets.map((a) => a.assetId));

  const importable = deduped.filter((a) => !existingIds.has(a.assetId));
  const imported = deduped.filter((a) => existingIds.has(a.assetId));

  return {
    allImportTypeAssets: deduped,
    importableAssets: importable,
    importedAssets: imported,
  };
};

const getAssetSourceKey = (asset = {}) => asset.sourceAssetId || asset.assetId;

const isSourceLinkedAsset = (asset = {}) =>
  Boolean(
    asset.importedFromSource ||
    asset.sourceAssetId ||
    asset.source ||
    asset.faRegisterSource
  );

function processAssetsWithPrincipalDetails(
  caSettings,
  dataRows,
  yearOfAssessment
) {
  const overallPrincipalDetails = {};

  dataRows.forEach((asset) => {
    const {
      assetId,
      caType,
      principalPaid,
      noOfYears,
      deferredYears = [],
      isCurrentYearDisposed = false,
      initialAllowanceRate = undefined,
      cost,
    } = asset;

    const principalDetails = {};

    const caSetting = getCaSettingByKey(caSettings, caType);

    let breakdown = [];
    const existBreakdownDefined =
      caSetting &&
      caSetting !== null &&
      caSetting?.numberOfYears === noOfYears &&
      caSetting?.AAbreakdown?.length > 1;
    if (existBreakdownDefined) {
      if (caType === "S19A(1) 3 years")
        breakdown = [33.333333, 33.333333, 33.333334];
      else breakdown = caSetting.AAbreakdown;
    }

    // Ensure structure exists for this caType and assetId
    if (!overallPrincipalDetails[caType]) {
      overallPrincipalDetails[caType] = {};
    }
    if (!overallPrincipalDetails[caType][assetId]) {
      overallPrincipalDetails[caType][assetId] = {};
      overallPrincipalDetails[caType][assetId].isCurrentYearDisposed =
        isCurrentYearDisposed;
    }

    if (caType === "S19") {
      if (initialAllowanceRate) {
        function allocateAmounts(totalCost, years) {
          // Backward-compatible safety:
          // legacy data may contain decimal/invalid values that previously
          // crashed schedule rendering.
          const safeTotalCost = Number.isFinite(Number(totalCost))
            ? Math.max(0, Number(totalCost))
            : 0;
          const safeYears =
            Number.isInteger(Number(years)) && Number(years) > 0
              ? Number(years)
              : 1;

          const baseClaim = Math.floor(safeTotalCost / safeYears);
          const allocations = [];
          let allocatedSoFar = 0;

          for (let i = 0; i < safeYears; i++) {
            const remainingClaim = Math.max(0, safeTotalCost - allocatedSoFar);
            let claim =
              i === safeYears - 1
                ? remainingClaim
                : Math.min(baseClaim, remainingClaim);

            claim = parseFloat(claim.toFixed(2));
            allocatedSoFar = parseFloat((allocatedSoFar + claim).toFixed(2));
            allocations.push(claim);
          }

          return {
            totalCost: safeTotalCost,
            years: safeYears,
            perYear: allocations,
            totalAllocated: allocations.reduce((a, b) => a + b, 0),
          };
        }

        const iaRate = initialAllowanceRate / 100;
        const normalizedCost = Number(cost);
        const aaCost = Math.max(
          0,
          Number.isFinite(normalizedCost) ? normalizedCost * (1 - iaRate) : 0
        );
        const aaClaims = allocateAmounts(aaCost, noOfYears);

        const resolveClaimYear = (fromYear) => {
          let year = fromYear;
          while (deferredYears.includes(year)) {
            year++;
          }
          return year;
        };

        const pushPrincipalRecord = (calendarYear, record) => {
          if (!principalDetails[calendarYear]) {
            principalDetails[calendarYear] = [];
          }
          principalDetails[calendarYear].push(record);
        };

        const isDisposedYear = (year) =>
          isCurrentYearDisposed && year >= yearOfAssessment;

        const ensureDeferredPathRecords = (fromYear, claimYear, aaClaim) => {
          for (let year = fromYear + 1; year < claimYear; year++) {
            if (!deferredYears.includes(year)) continue;

            const hasDeferredFrom = (principalDetails[year] || []).some(
              (entry) => entry.from === fromYear && entry.deferred
            );
            if (hasDeferredFrom) continue;

            const pathRecord = {
              from: fromYear,
              amount: 0,
              ia: 0,
              aa: aaClaim,
              deferred: true,
            };
            if (isDisposedYear(year)) {
              pathRecord.disposed = true;
            }
            pushPrincipalRecord(year, pathRecord);
          }
        };

        const sortedPrincipalPaid = [...principalPaid].sort(
          (a, b) => a.year - b.year
        );
        const iaClaimPaymentIndexes = [];
        const totalIaClaimableCost = sortedPrincipalPaid.reduce(
          (sum, payment, index) => {
            const isIaClaimable = !isDisposedYear(payment.year);

            if (!isIaClaimable) return sum;

            iaClaimPaymentIndexes.push(index);
            const numericAmount = Number(payment.amount);
            return (
              sum +
              (Number.isFinite(numericAmount) ? Math.max(0, numericAmount) : 0)
            );
          },
          0
        );
        const totalIaClaim = Math.max(0, totalIaClaimableCost * iaRate);
        const lastIaClaimPaymentIndex =
          iaClaimPaymentIndexes[iaClaimPaymentIndexes.length - 1];
        let iaClaimedSoFar = 0;

        const paymentYears = sortedPrincipalPaid.map((p) => p.year);
        const startYear =
          paymentYears.length > 0
            ? paymentYears[0]
            : yearOfAssessment - noOfYears + 1;
        const slotYears = Array.from(
          { length: noOfYears },
          (_, idx) => startYear + idx
        );
        sortedPrincipalPaid.forEach((payment, index) => {
          const { amount, year: originYear } = payment;
          const claimYear = resolveClaimYear(originYear);
          const isOriginDeferred = deferredYears.includes(originYear);
          const aaClaim = aaClaims.perYear[index];
          let iaClaim = 0;

          // IA is claimed in the payment year even when AA is deferred.
          if (!isDisposedYear(originYear)) {
            const numericAmount = Number(amount);
            const remainingIa = Math.max(0, totalIaClaim - iaClaimedSoFar);
            iaClaim =
              index === lastIaClaimPaymentIndex
                ? remainingIa
                : Math.min(
                    Math.ceil(
                      Number.isFinite(numericAmount) ? numericAmount * iaRate : 0
                    ),
                    remainingIa
                  );
            iaClaimedSoFar += iaClaim;
          }

          if (isOriginDeferred) {
            const deferredRecord = {
              from: originYear,
              amount: 0,
              ia: iaClaim,
              aa: aaClaim,
              deferred: true,
            };
            if (isDisposedYear(originYear)) {
              deferredRecord.disposed = true;
            }
            pushPrincipalRecord(originYear, deferredRecord);
          }

          ensureDeferredPathRecords(originYear, claimYear, aaClaim);

          let amountToAssign = 0;
          const claimRecord = {
            from: originYear,
            amount: 0,
            ia: isOriginDeferred ? 0 : iaClaim,
            aa: aaClaim,
          };

          if (claimYear > originYear) {
            claimRecord.carried = true;
          }

          if (isDisposedYear(claimYear)) {
            claimRecord.disposed = true;
            claimRecord.ia = 0;
          } else {
            claimRecord.amount = aaClaim;
            amountToAssign = aaClaim;
          }

          pushPrincipalRecord(claimYear, claimRecord);

          if (!overallPrincipalDetails[caType][assetId][claimYear]) {
            overallPrincipalDetails[caType][assetId][claimYear] = [];
          }
          overallPrincipalDetails[caType][assetId][claimYear].push({
            from: originYear,
            amount: amountToAssign,
          });
        });

        slotYears.forEach((slotYear, index) => {
          const claimYear = resolveClaimYear(slotYear);
          const aaClaim = aaClaims.perYear[index];
          const hasSlotRecord = (principalDetails[slotYear] || []).some(
            (entry) => entry.from === slotYear
          );

          if (!hasSlotRecord) {
            const slotRecord = {
              from: slotYear,
              amount: 0,
              ia: 0,
              aa: aaClaim,
            };

            if (deferredYears.includes(slotYear)) {
              slotRecord.deferred = true;
            } else if (isDisposedYear(slotYear)) {
              slotRecord.disposed = true;
            } else {
              slotRecord.amount = aaClaim;
            }

            pushPrincipalRecord(slotYear, slotRecord);

            if (!overallPrincipalDetails[caType][assetId][slotYear]) {
              overallPrincipalDetails[caType][assetId][slotYear] = [];
            }
            overallPrincipalDetails[caType][assetId][slotYear].push({
              from: slotYear,
              amount: 0,
            });
          }

          ensureDeferredPathRecords(slotYear, claimYear, aaClaim);

          if (claimYear === slotYear) return;

          const hasClaimRecord = (principalDetails[claimYear] || []).some(
            (entry) => entry.from === slotYear && !entry.deferred
          );
          if (hasClaimRecord) return;

          const claimRecord = {
            from: slotYear,
            amount: 0,
            ia: 0,
            aa: aaClaim,
          };

          if (claimYear > slotYear) {
            claimRecord.carried = true;
          }

          if (isDisposedYear(claimYear)) {
            claimRecord.disposed = true;
          } else {
            claimRecord.amount = aaClaim;
          }

          pushPrincipalRecord(claimYear, claimRecord);

          if (!overallPrincipalDetails[caType][assetId][claimYear]) {
            overallPrincipalDetails[caType][assetId][claimYear] = [];
          }
          overallPrincipalDetails[caType][assetId][claimYear].push({
            from: slotYear,
            amount: claimRecord.amount,
          });
        });

        deferredYears
          .filter(
            (year) => year >= startYear && !principalDetails[year]?.length
          )
          .forEach((year) => {
            pushPrincipalRecord(year, {
              from: year,
              amount: 0,
              ia: 0,
              aa: 0,
              deferred: true,
            });
          });
      }
    } else {
      principalPaid.forEach((payment) => {
        const { amount, year: originYear } = payment;

        let spreadCount = 0;
        let targetYear = originYear;
        let claimHistory = [];

        while (
          spreadCount < noOfYears &&
          !(isCurrentYearDisposed && targetYear > yearOfAssessment)
        ) {
          const isDeferred = deferredYears.includes(targetYear);
          let record = { from: originYear, amount: 0 };

          if (isDeferred) {
            record.deferred = true;
          } else if (isCurrentYearDisposed && yearOfAssessment === targetYear) {
            record.disposed = true;
          } else {
            let amountToAssign = 0;

            if (!(isCurrentYearDisposed && targetYear === yearOfAssessment)) {
              if (initialAllowanceRate && initialAllowanceRate > 0) {
                const iaRate = initialAllowanceRate / 100;
                const baRate = 1 - iaRate;
                const priorClaims = claimHistory.reduce(
                  (sum, r) => sum + r.amount,
                  0
                );
                const claimsSoFar = claimHistory.length;

                if (targetYear === originYear) {
                  const ia = amount * iaRate;
                  const baBase = Math.ceil((amount * baRate) / noOfYears);
                  const ba = baBase;
                  amountToAssign = parseFloat((ia + ba).toFixed(2));
                } else if (claimsSoFar === noOfYears - 1) {
                  // Final year – assign remaining amount
                  amountToAssign = parseFloat(
                    (amount - priorClaims).toFixed(2)
                  );
                } else {
                  // Intermediate year
                  const remaining = amount - priorClaims;
                  const remainingYears = noOfYears - claimsSoFar;
                  const baseYearAmount = Math.ceil(remaining / remainingYears);
                  amountToAssign = parseFloat(baseYearAmount.toFixed(2));
                }
              } else {
                // Regular split with rounding fix
                const priorClaims = claimHistory.reduce(
                  (sum, r) => sum + r.amount,
                  0
                );
                const claimsSoFar = claimHistory.length;

                if (claimsSoFar === noOfYears - 1) {
                  // Last year – assign remainder
                  amountToAssign = parseFloat(
                    (amount - priorClaims).toFixed(2)
                  );
                } else {
                  let baseYearAmount = Math.ceil(amount / noOfYears);

                  if (
                    existBreakdownDefined &&
                    breakdown &&
                    breakdown.length > 1
                  ) {
                    if (caType !== "S19A(1) 2 years")
                      baseYearAmount = Math.abs(
                        Math.round(amount * (breakdown[claimsSoFar] / 100))
                      );
                    else {
                      baseYearAmount = Math.abs(
                        Math.round(amount * (breakdown[noOfYears - 1] / 100))
                      );
                    }
                  }

                  amountToAssign = parseFloat(baseYearAmount.toFixed(2));
                }
              }
            }

            record.amount = amountToAssign;

            // Store in overallPrincipalDetails under caType → assetId → year
            if (!overallPrincipalDetails[caType][assetId][targetYear]) {
              overallPrincipalDetails[caType][assetId][targetYear] = [];
            }

            overallPrincipalDetails[caType][assetId][targetYear].push({
              from: originYear,
              amount: amountToAssign,
            });

            spreadCount++;
            claimHistory.push(record);
          }

          // Store in asset's own principalDetails
          if (!principalDetails[targetYear]) principalDetails[targetYear] = [];
          principalDetails[targetYear].push(record);

          targetYear++;
        }
      });
    }

    asset.principalDetails = principalDetails;
  });

  return {
    assets: dataRows,
    overallPrincipalDetails,
  };
}

function sumByFrom(data, year, yearOfAssessment, caType) {
  let total = 0;

  if (caType !== "S19") {
    for (const [key, arr] of Object.entries(data)) {
      if (!Array.isArray(arr)) continue;

      if (key < yearOfAssessment) {
        for (const item of arr) {
          if (item.from === year) {
            total += item.amount;
          }
        }
      }
    }
  } else {
    for (const [key, arr] of Object.entries(data)) {
      if (!Array.isArray(arr)) continue;

      if (key < yearOfAssessment) {
        for (const item of arr) {
          if (item.from === year) {
            let subtotal = item.ia + item.aa;
            total += subtotal;
          }
        }
      }
    }
  }

  return total;
}

function generateSummaryData(assets, caSettings, yearOfAssessment) {
  const summaryData = [];
  const caTypeTotals = {};

  if (!assets || !yearOfAssessment) {
    return { summaryData, caTypeTotals };
  }

  const grouped = {};

  assets.forEach((asset) => {
    const {
      assetId,
      assetName,
      cost,
      caType,
      noOfYears,
      principalPaid = [],
      principalDetails = [],
      deferredYears = [],
      isCurrentYearDisposed,
    } = asset;

    const currentYaDeferred = deferredYears.includes(yearOfAssessment);
    const deferredPerYear = Object.entries(principalDetails).reduce(
      (acc, [, entries]) => {
        entries.forEach((item) => {
          if (item.deferred && item.from) {
            acc[item.from] = (acc[item.from] || 0) + 1;
          }
        });
        return acc;
      },
      {}
    );

    const paymentsByYear = (principalPaid || []).reduce((acc, payment) => {
      const year = payment.year;
      acc[year] = (acc[year] || 0) + (payment.amount || 0);
      return acc;
    }, {});

    const allClaimYears = Object.keys(principalDetails || {})
      .map((year) => Number(year))
      .sort((a, b) => a - b);

    const firstClaimYear = allClaimYears.length > 0 ? allClaimYears[0] : null;

    if (caType === "S19") {
      // Only show current year for S19
      const year = yearOfAssessment;
      const calculatedNoOfYearsBf = isCurrentYearDisposed
        ? 1
        : firstClaimYear
          ? Math.max(
              0,
              Math.min(
                noOfYears,
                noOfYears - (yearOfAssessment - firstClaimYear)
              )
            )
          : 0;

      const entries = principalDetails[year] || [];
      const currentYearDeferred =
        entries.find((entry) => entry.from === year)?.deferred || false;

      entries.forEach((entry) => {
        const deferred = entry.deferred === true;
        const isDisposedEntry =
          isCurrentYearDisposed &&
          year === yearOfAssessment &&
          entry.disposed === true;
        const ia = isDisposedEntry
          ? 0
          : entry.from === year
            ? entry.ia || 0
            : 0;
        const aa = entry.aa || 0;
        let iaDisplay = !(entry.from === year) ? "Forfeit" : null;

        if (isDisposedEntry) iaDisplay = "Forfeit";

        const principalPaidAmount = paymentsByYear[entry.from] || 0;

        const totalPaid = principalPaidAmount;
        const priorPaid = sumByFrom(
          principalDetails,
          year,
          yearOfAssessment,
          caType
        );
        const twdvBf = totalPaid - priorPaid;

        let disposedAmount = 0;
        if (isCurrentYearDisposed && year === yearOfAssessment)
          disposedAmount = twdvBf;

        if (!grouped[caType]) {
          grouped[caType] = {};
        }

        const groupKey = `${year}_${noOfYears}_${entry.from}_${deferred ? "deferred" : "normal"}_${calculatedNoOfYearsBf}`;

        if (!grouped[caType][groupKey]) {
          grouped[caType][groupKey] = {
            caType,
            caSummary: getCaSummary(caSettings, caType),
            year: entry.from,
            isYearDeferred: deferred,
            deferred: currentYearDeferred,
            disposed: isCurrentYearDisposed,
            noOfYears,
            noOfYearsBf: calculatedNoOfYearsBf,
            principalPaid: 0,
            ia: 0,
            aa: 0,
            iaDisplay: null,
            twdvBf: 0,
            assets: [],
            disposedAmount: 0,
          };
        }

        grouped[caType][groupKey].principalPaid += principalPaidAmount || 0;
        grouped[caType][groupKey].ia -= ia;
        grouped[caType][groupKey].aa -= aa;
        if (iaDisplay === "Forfeit") {
          grouped[caType][groupKey].iaDisplay = "Forfeit";
        }

        if (isCurrentYearDisposed)
          grouped[caType][groupKey].disposedAmount -= aa;
        else grouped[caType][groupKey].disposedAmount -= 0;
        grouped[caType][groupKey].assets.push({
          assetId,
          assetName,
          cost,
          amount: principalPaidAmount,
          isDisposed: isCurrentYearDisposed,
        });
      });
    } else {
      principalPaid.forEach((pp) => {
        const { year, amount } = pp;

        // skip if future year
        if (year > yearOfAssessment) return;

        // no of years b/f
        const noOfYearsBf = Math.min(
          noOfYears,
          noOfYears - (yearOfAssessment - year) + (deferredPerYear[year] || 0)
        );
        if (noOfYearsBf <= 0) return;

        // twdv b/f = total principal paid – prior year payments (< yearOfAssessment)
        const totalPaid = amount;

        const priorPaid = sumByFrom(
          principalDetails,
          year,
          yearOfAssessment,
          caType
        );

        const twdvBf = totalPaid - priorPaid;

        // check if this year is deferred
        const isDeferred = currentYaDeferred
          ? (principalDetails[yearOfAssessment] || []).some(
              (item) => item.from === year
            )
          : false;

        const currentYearEntries = principalDetails[year] || [];
        const ia = currentYearEntries.reduce((sum, item) => {
          if (item.from !== year) return sum;
          return sum + (item.ia || 0);
        }, 0);
        const aa = currentYearEntries.reduce((sum, item) => {
          return sum + (item.aa || 0);
        }, 0);

        let disposedAmount = 0;
        if (isCurrentYearDisposed) disposedAmount = twdvBf;

        // init caType
        if (!grouped[caType]) {
          grouped[caType] = {};
        }

        // key by year + deferred flag + noOfYearsBf
        const groupKey = `${year}_${noOfYears}_${
          isDeferred ? "deferred" : "normal"
        }_${noOfYearsBf}`;

        if (!grouped[caType][groupKey]) {
          grouped[caType][groupKey] = {
            caType,
            caSummary: getCaSummary(caSettings, caType),
            year,
            isYearDeferred: (principalDetails[year] || []).some(
              (item) => item.from === year && item.deferred === true
            ),
            deferred: isDeferred,
            noOfYears,
            noOfYearsBf,
            principalPaid: 0,
            ia: 0,
            aa: 0,
            twdvBf: 0,
            assets: [],
            disposedAmount: 0,
          };
        }

        grouped[caType][groupKey].principalPaid += amount || 0;
        grouped[caType][groupKey].ia += ia;
        grouped[caType][groupKey].aa += aa;
        grouped[caType][groupKey].disposedAmount -= disposedAmount || 0;

        grouped[caType][groupKey].twdvBf += twdvBf;
        grouped[caType][groupKey].assets.push({
          assetId,
          assetName,
          cost,
          amount,
          isDisposed: isCurrentYearDisposed,
        });
      });
    }
  });

  Object.keys(grouped).forEach((caType) => {
    const sortedEntries = Object.values(grouped[caType]).sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      if (a.deferred !== b.deferred) return a.deferred ? 1 : -1; // normal first
      return a.noOfYearsBf - b.noOfYearsBf;
    });

    // rebuild as object to keep same structure
    grouped[caType] = {};
    sortedEntries.forEach((entry, idx) => {
      grouped[caType][`key_${idx}`] = entry;
    });
  });

  Object.entries(grouped).forEach(([caType, groupEntries]) => {
    let caTypeTotal = 0;

    Object.values(groupEntries).forEach((data) => {
      if (caType === "S19") {
        caTypeTotal += data.principalPaid;
      } else {
        caTypeTotal += data.twdvBf;
      }
    });

    if (caTypeTotal > 0) {
      caTypeTotals[caType] = caTypeTotal;
    }
  });

  return { summaryData: grouped, caTypeTotals };
}

const assetDefaults = (asset, globalDeferredYears, categories) => ({
  ...asset,
  assetId: asset.assetId ?? uuidv4(),
  categoryId: asset.categoryId ?? undefined,
  categoryName: categories[asset.categoryId] ?? "",
  caType: asset.caType || "",
  noOfYears: asset.noOfYears || "",
  principalPaid: asset.principalPaid || [],
  initialAllowanceRate: asset.initialAllowanceRate ?? undefined,
  deferredYears: [...globalDeferredYears],
});

const HirePurchaseSchedule = ({
  companyName,
  uen,
  yearOfAssessment,
  financialYearStart,
  financialYearEnd,
  schedule,
  scheduleDetails,
  saveSchedule,
  deleteSchedule,
  canEditSchedule = true,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const handleError = useErrorDialog();

  const [viewPrincipalPaid, setViewPrincipalPaid] = useState(false);
  const [editAsset, setEditAsset] = useState(false);

  // Use tab persistence hook for selected asset index
  const { selectedTabIndex, setSelectedTabIndex } = useTabPersistence(
    `hire-purchase-${schedule?.id || "default"}-selected-tab`,
    0
  );

  const [selectedItem, setSelectedItem] = useState(null);
  const [showDecimals, setShowDecimals] = useState(false);
  const [isCreatingNew, setIsCreatingNew] = useState(false);

  useEffect(() => {
    setShowDecimals(false);
  }, [selectedItem?.assetId]);

  const [caTypeOptions, setCaTypeOptions] = useState([]);
  const [caSettings, setCaSettings] = useState([]);

  const [categories, setCategories] = useState([]);
  const [categoryOption, setCategoryOption] = useState([]);
  const [allImportTypeAssets, setAllImportTypeAssets] = useState([]);
  const [importableAssets, setImportableAssets] = useState([]);
  const [importedAssets, setImportedAssets] = useState([]);
  const [importAssetsDialogOpen, setImportAssetsDialogOpen] = useState(false);

  const [summaryData, setSummaryData] = useState([]);
  const [caTypeTotals, setCaTypeTotals] = useState({});

  const [notes, setNotes] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");

  const [assets, setAssets] = useState([]);
  const [globalDeferredYears, setGlobalDeferredYears] = useState([]);
  const [overallPrincipalDetails, setOverAllPrincipalDetails] = useState({});
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState("tabs");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState(null);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const unavailableSourceAssets = useMemo(() => {
    const availableSourceIds = new Set(
      allImportTypeAssets
        .map((asset) => getAssetSourceKey(asset))
        .filter(Boolean)
    );

    return assets
      .map((asset, index) => ({ asset, index }))
      .filter(({ asset }) => {
        const sourceKey = getAssetSourceKey(asset);
        return (
          isSourceLinkedAsset(asset) &&
          Boolean(sourceKey) &&
          !availableSourceIds.has(sourceKey)
        );
      });
  }, [allImportTypeAssets, assets]);

  // Excel upload dialog state
  const [excelDialogOpen, setExcelDialogOpen] = useState(false);
  const handleOpenExcelUploadDialog = () => {
    setExcelDialogOpen(true);
  };
  const handleCloseExcelUploadDialog = () => setExcelDialogOpen(false);

  // HP Tool dialog state
  const [hpToolDialogOpen, setHpToolDialogOpen] = useState(false);
  const handleOpenHpToolDialog = () => {
    setHpToolDialogOpen(true);
  };
  const [hpToolsLoading, setHpToolsLoading] = useState(false);
  const handleCloseHpToolDialog = () => setHpToolDialogOpen(false);

  const [codePrefix, setCodePrefix] = useState("HPA");
  const [assetCodeList, setAssetCodeList] = useState([]);
  useEffect(() => {
    const usedCodes = new Set();

    const generated = assets.map((asset, index) => {
      const existing = assetCodeList.find((a) => a.assetId === asset.assetId);

      if (existing && existing.assetCode) {
        usedCodes.add(existing.assetCode);
        return { assetId: asset.assetId, assetCode: existing.assetCode };
      }

      let code = `${codePrefix}${String(index + 1).padStart(3, "0")}`;
      while (usedCodes.has(code)) {
        code = `${codePrefix}${String(
          Math.floor(Math.random() * 100000)
        ).padStart(5, "0")}`;
      }

      usedCodes.add(code);
      return { assetId: asset.assetId, assetCode: code };
    });

    setAssetCodeList(generated);
  }, [assets]);

  // Drag and Drop sensors with activation constraints
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Only start dragging after moving 8px
      },
    })
  );

  const hasRunRef = useRef(false);
  useEffect(() => {
    if (hasRunRef.current) return;
    hasRunRef.current = true;

    if (schedule) {
      const hpCaSettings = schedule.caSettings.filter(
        (setting) => setting.caType !== "S19D"
      );
      setCaSettings(hpCaSettings);
      setCaTypeOptions(getCaKeys(hpCaSettings));
      setNotes(schedule.notes);
      setReviewNotes(schedule.reviewNotes);
    }

    if (scheduleDetails?.dataRows) {
      setGlobalDeferredYears(
        scheduleDetails.dataRows.settings?.deferredYears || []
      );

      setCodePrefix(scheduleDetails.dataRows.settings?.codePrefix || "HPA");
      setAssetCodeList(scheduleDetails.dataRows.settings?.assetCodeList || []);

      const result = processAssetsWithPrincipalDetails(
        schedule.caSettings,
        scheduleDetails.dataRows.assets,
        yearOfAssessment
      );
      let assets = result.assets;
      setOverAllPrincipalDetails(result.overallPrincipalDetails);

      if (scheduleDetails.categories?.length > 0) {
        const catMap = Object.fromEntries(
          scheduleDetails.categories.map((c) => [c.assetId, c.assetCategory])
        );
        const categoryOptions = scheduleDetails.categories.map((c) => ({
          value: c.assetId,
          label: c.assetCategory,
        }));
        assets = result.assets.map((asset) => ({
          ...asset,
          categoryName: catMap[asset.categoryId] || null,
        }));
        setCategories(catMap);
        setCategoryOption(categoryOptions);
      }
      setAssets(assets);

      // also update item if index is still valid
      if (result.assets.length > 0 && selectedTabIndex < result.assets.length) {
        setSelectedItem(result.assets[selectedTabIndex]);
      }
    }
  }, []);

  useEffect(() => {
    setSelectedItem((prev) => {
      if (assets.length > 0 && selectedTabIndex < assets.length) {
        return assets[selectedTabIndex];
      }
      return prev;
    });
  }, [selectedTabIndex, assets]);

  useEffect(() => {
    const { allImportTypeAssets, importableAssets, importedAssets } =
      processImportableAssets(
        scheduleDetails?.additionsHpItems || [],
        categories,
        assets
      );

    setAllImportTypeAssets(allImportTypeAssets);
    setImportableAssets(importableAssets);
    setImportedAssets(importedAssets);
  }, [assets, categories, scheduleDetails?.additionsHpItems]);

  useEffect(() => {
    const { summaryData, caTypeTotals } = generateSummaryData(
      assets,
      caSettings,
      yearOfAssessment
    );

    setSummaryData(summaryData);
    setCaTypeTotals(caTypeTotals);
  }, [assets]);

  const handleAddAsset = () => {
    setIsCreatingNew(true);
    setSelectedItem(null);
    setEditAsset(true);
  };

  const handleCloseAssetInfo = () => {
    setEditAsset(false);
    if (isCreatingNew) {
      setSelectedItem(null);
      setSelectedTabIndex(null);
    }
    setIsCreatingNew(false);
  };

  const handleAssetSave = (updatedAsset) => {
    let latestAsset = assets;

    if (isCreatingNew) {
      let deferredYears = updatedAsset.deferredYears || [];
      if (!deferredYears || deferredYears?.length === 0) {
        deferredYears = [...globalDeferredYears];
      }

      const newAssets = [
        ...assets,
        assetDefaults(updatedAsset, deferredYears, categories),
      ];
      setAssets(newAssets);
      setSelectedTabIndex(newAssets.length - 1);
      setSelectedItem(newAssets[newAssets.length - 1]);
      latestAsset = newAssets;
    } else {
      const existingAsset = assets[selectedTabIndex];

      if (areAssetsEqual(existingAsset, updatedAsset)) {
        // No update needed
        setEditAsset(false);
        setIsCreatingNew(false);
        return;
      }
      updatedAsset.categoryName = categories[updatedAsset.categoryId] || null;

      // Proceed to update asset
      const updatedAssets = [...assets];
      updatedAssets[selectedTabIndex] = updatedAsset;
      setAssets(updatedAssets);
      setSelectedItem(updatedAsset);
      latestAsset = updatedAssets;
    }
    setEditAsset(false);
    setIsCreatingNew(false);

    const result = processAssetsWithPrincipalDetails(
      caSettings,
      latestAsset,
      yearOfAssessment
    );
    setAssets(result.assets);
    setOverAllPrincipalDetails(result.overallPrincipalDetails);
  };

  const handleEditAsset = () => {
    setIsCreatingNew(false);
    setEditAsset(true);
  };

  const handleDeferralChange = (newDeferredYears) => {
    // Update the selected asset with new deferredYears
    const assetToUpdate = {
      ...selectedItem,
      deferredYears: newDeferredYears,
    };

    // Update the assets array
    const updatedAssets = [...assets];
    updatedAssets[selectedTabIndex] = assetToUpdate;

    // Recalculate principal details with the new deferred years
    const result = processAssetsWithPrincipalDetails(
      caSettings,
      updatedAssets,
      yearOfAssessment
    );

    // Update the selected item and assets state
    setAssets(result.assets);
    setSelectedItem(result.assets[selectedTabIndex]);
    setOverAllPrincipalDetails(result.overallPrincipalDetails);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!active?.id || !over?.id || active.id === over.id) return;

    const oldIndex = assets.findIndex((asset) => asset.assetId === active.id);
    const newIndex = assets.findIndex((asset) => asset.assetId === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const reorderedAssets = arrayMove(assets, oldIndex, newIndex);
    setAssets(reorderedAssets);

    // Update selectedTabIndex if the selected asset was moved
    if (selectedTabIndex === oldIndex) {
      setSelectedTabIndex(newIndex);
      setSelectedItem(reorderedAssets[newIndex]);
    } else if (selectedTabIndex > oldIndex && selectedTabIndex <= newIndex) {
      setSelectedTabIndex(selectedTabIndex - 1);
    } else if (selectedTabIndex < oldIndex && selectedTabIndex >= newIndex) {
      setSelectedTabIndex(selectedTabIndex + 1);
    }
  };

  const handleDeleteAsset = (index) => {
    setAssetToDelete({ index, asset: assets[index] });
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteAsset = () => {
    if (!assetToDelete) return;

    const { index } = assetToDelete;
    const updatedAssets = assets.filter((_, i) => i !== index);

    // Recalculate principal details
    const result = processAssetsWithPrincipalDetails(
      caSettings,
      updatedAssets,
      yearOfAssessment
    );

    setAssets(result.assets);
    setOverAllPrincipalDetails(result.overallPrincipalDetails);

    // Update selected index if needed
    if (selectedTabIndex === index) {
      setSelectedTabIndex(null);
      setSelectedItem(null);
    } else if (selectedTabIndex > index) {
      setSelectedTabIndex(selectedTabIndex - 1);
      setSelectedItem(result.assets[selectedTabIndex - 1]);
    }

    // Close dialog and reset state
    setDeleteConfirmOpen(false);
    setAssetToDelete(null);
  };

  const cancelDeleteAsset = () => {
    setDeleteConfirmOpen(false);
    setAssetToDelete(null);
  };

  const handleDuplicateAsset = (assetId) => {
    const assetToDuplicate = assets.find((asset) => asset.assetId === assetId);
    if (!assetToDuplicate) return;
    const duplicatedAsset = {
      ...assetToDuplicate,
      assetId: uuidv4(),
      assetName: `${assetToDuplicate.assetName} (Copy)`,
    };

    const newAssets = [...assets, duplicatedAsset];

    // Recalculate principal details
    const result = processAssetsWithPrincipalDetails(
      caSettings,
      newAssets,
      yearOfAssessment
    );
    setAssets(result.assets);
    setOverAllPrincipalDetails(result.overallPrincipalDetails);
  };

  const handleBulkDeleteAssets = (assetIds) => {
    const updatedAssets = assets.filter(
      (asset) => !assetIds.includes(asset.assetId)
    );
    const result = processAssetsWithPrincipalDetails(
      caSettings,
      updatedAssets,
      yearOfAssessment
    );
    setAssets(result.assets);
    setOverAllPrincipalDetails(result.overallPrincipalDetails);
    setSelectedTabIndex(null);
    setSelectedItem(null);
  };

  const handleBulkDisposeAssets = (assetIds) => {
    let updatedAssets = assets.map((asset) => {
      if (assetIds.includes(asset.assetId)) {
        return {
          ...asset,
          isCurrentYearDisposed: !asset.isCurrentYearDisposed,
        };
      }
      return asset;
    });

    const result = processAssetsWithPrincipalDetails(
      caSettings,
      updatedAssets,
      yearOfAssessment
    );
    setAssets(result.assets);

    if (selectedItem && assetIds.includes(selectedItem.assetId)) {
      const updatedSelected = result.assets.find(
        (a) => a.assetId === selectedItem.assetId
      );
      setSelectedItem(updatedSelected || null);
    }

    setOverAllPrincipalDetails(result.overallPrincipalDetails);
  };

  const handleMarkAssetIndependent = (assetId) => {
    const assetIndex = assets.findIndex((asset) => asset.assetId === assetId);
    if (assetIndex === -1) return;

    const nextAssetId = uuidv4();
    const updatedAssets = assets.map((asset, index) => {
      if (index !== assetIndex) return asset;

      const {
        importedFromSource,
        source,
        sourceSchedule,
        sourceAssetId,
        faRegisterSource,
        ...independentAsset
      } = asset;

      return {
        ...independentAsset,
        assetId: nextAssetId,
      };
    });

    const result = processAssetsWithPrincipalDetails(
      caSettings,
      updatedAssets,
      yearOfAssessment
    );

    setAssets(result.assets);
    setOverAllPrincipalDetails(result.overallPrincipalDetails);

    if (selectedTabIndex === assetIndex || selectedItem?.assetId === assetId) {
      setSelectedTabIndex(assetIndex);
      setSelectedItem(result.assets[assetIndex]);
    }
  };

  const handleSettingsSave = (
    newGlobalDeferredYears,
    yearsToApplyToExisting = [],
    newCodePrefix = codePrefix,
    newAssetCodeList = assetCodeList
  ) => {
    setGlobalDeferredYears(newGlobalDeferredYears);
    setCodePrefix(newCodePrefix);
    setAssetCodeList(newAssetCodeList);

    const updatedAssets = assets.map((asset) => {
      const existingDeferredYears = asset.deferredYears || [];

      // Decide which years to apply to each asset
      const yearsToAdd = yearsToApplyToExisting;

      // Merge and deduplicate
      let mergedDeferredYears = Array.from(
        new Set([...existingDeferredYears, ...yearsToAdd])
      );

      if (asset.isCurrentYearDisposed) {
        mergedDeferredYears = mergedDeferredYears.filter(
          (y) => y < yearOfAssessment
        );
      }

      return {
        ...asset,
        deferredYears: mergedDeferredYears,
      };
    });

    // Recalculate principal details
    const result = processAssetsWithPrincipalDetails(
      caSettings,
      updatedAssets,
      yearOfAssessment
    );

    setAssets(result.assets);
    setOverAllPrincipalDetails(result.overallPrincipalDetails);

    // Update selected item if it exists
    if (selectedItem && selectedTabIndex !== null) {
      setSelectedItem(result.assets[selectedTabIndex]);
    }
  };

  const handleSave = () => {
    schedule.notes = notes;
    schedule.reviewNotes = reviewNotes;

    saveSchedule(schedule, {
      dataRows: {
        assets: assets.map((asset) => ({
          assetId: asset.assetId,
          assetName: asset.assetName,
          categoryId: asset.categoryId,
          caType: asset.caType,
          noOfYears: asset.noOfYears,
          cost: asset.cost,
          initialAllowanceRate: asset.initialAllowanceRate,
          deferredYears: asset.deferredYears || [],
          isCurrentYearDisposed: asset.isCurrentYearDisposed || false,
          principalPaid: asset.principalPaid || [],
          principalDetails: asset.principalDetails || {},
          importedFromSource: asset.importedFromSource || false,
          source: asset.source,
          sourceSchedule: asset.sourceSchedule,
          sourceAssetId: asset.sourceAssetId,
          faRegisterSource: asset.faRegisterSource || false,
        })),
        settings: {
          deferredYears: globalDeferredYears,
          assetCodeList: assetCodeList,
          codePrefix: codePrefix,
        },
      },
      computedData: {
        overallPrincipalDetails: overallPrincipalDetails,
        totalAssets: assets.length,
        hasDisposedAssets: assets.some((asset) => asset.isCurrentYearDisposed),
        totalDeferredYears: globalDeferredYears.length,
        summary: summaryData,
        updatedAt: new Date(),
      },
      doc: JSON.stringify({
        assets,
        assetCodeList,
        summaryData,
        caTypeTotals,
        notes,
        reviewNotes,
      }),
    });
  };

  const onImportFromAdditions = (selectedAssets) => {
    if (!selectedAssets || selectedAssets.length === 0) return;

    const imported = selectedAssets.map((asset) =>
      assetDefaults(
        {
          ...asset,
          importedFromSource: true,
          sourceAssetId: getAssetSourceKey(asset),
        },
        globalDeferredYears,
        categories,
        assets
      )
    );

    const result = processAssetsWithPrincipalDetails(
      caSettings,
      [...assets, ...imported],
      yearOfAssessment
    );
    setAssets(result.assets);
    setOverAllPrincipalDetails(result.overallPrincipalDetails);
  };

  const onExcelAddRows = (newRows) => {
    if (!newRows || newRows.length === 0) return;

    const newData = [...assets];
    const newAssetCodeList = [...assetCodeList];

    newRows.forEach((asset) => {
      const {
        assetCode,
        assetName,
        cost,
        caType,
        noOfYears,
        principalPaidYear,
        principalPaidAmount,
      } = asset;

      // Find if asset already exists
      const curAssetId =
        newAssetCodeList.find((a) => a.assetCode === assetCode)?.assetId ||
        null;
      const existingAsset =
        curAssetId !== null
          ? newData.find((a) => a.assetId === curAssetId)
          : null;

      if (!existingAsset) {
        // Create new asset
        const newAsset = assetDefaults(
          {
            assetName,
            cost,
            caType,
            noOfYears,
            principalPaid:
              principalPaidYear && principalPaidAmount
                ? [{ year: principalPaidYear, amount: principalPaidAmount }]
                : [],
          },
          globalDeferredYears,
          categories
        );

        // Add assetCode link separately
        newAssetCodeList.push({
          assetId: newAsset.assetId,
          assetCode: assetCode,
        });

        newData.push(newAsset);
      } else {
        // Update existing asset principalPaid
        if (principalPaidYear && principalPaidAmount) {
          const existingPayment = existingAsset.principalPaid.find(
            (p) => p.year === principalPaidYear
          );

          if (existingPayment) {
            existingPayment.amount = principalPaidAmount;
          } else {
            existingAsset.principalPaid.push({
              year: principalPaidYear,
              amount: principalPaidAmount,
            });
          }
        }

        // Update other fields
        existingAsset.assetName = assetName ?? existingAsset.assetName;
        existingAsset.cost = cost ?? existingAsset.cost;
        existingAsset.caType = caType ?? existingAsset.caType;
        existingAsset.noOfYears = noOfYears ?? existingAsset.noOfYears;
      }
    });

    // Save both assets and assetCodeList
    const result = processAssetsWithPrincipalDetails(
      caSettings,
      newData,
      yearOfAssessment
    );
    setAssets(result.assets);
    setOverAllPrincipalDetails(result.overallPrincipalDetails);
    setAssetCodeList(newAssetCodeList);

    handleCloseExcelUploadDialog();
  };

  const handleAddToHPSchedule = (results) => {
    if (!results || results.length === 0) return;

    setHpToolsLoading(true);

    const newData = [...assets];
    const newAssetCodeList = [...assetCodeList];

    /* -------------------------------------------------
     * STEP 1: Group rows into assets
     * ------------------------------------------------- */
    const grouped = {};

    results.forEach((row) => {
      const key = `${row.assetName}-${row.cost}-${row.caType}-${row.numberOfYears}`;

      if (!grouped[key]) {
        grouped[key] = {
          assetCode: row.assetCode || null,
          assetName: row.assetName,
          cost: row.cost,
          caType: row.caType,
          noOfYears: row.numberOfYears,
          initialAllowanceRate: row.initialAllowanceRate,
          principalPaid: [],
        };
      }

      if (row.ya && row.principalPaid) {
        grouped[key].principalPaid.push({
          year: row.ya,
          amount: row.principalPaid,
        });
      }
    });

    /* -------------------------------------------------
     * STEP 2: Prepare used asset codes
     * ------------------------------------------------- */
    const usedCodes = new Set(
      newAssetCodeList.map((a) => a.assetCode).filter(Boolean)
    );

    const generateAssetCode = (index) => {
      let code = `${codePrefix}${String(index + 1).padStart(3, "0")}`;
      while (usedCodes.has(code)) {
        code = `${codePrefix}${String(
          Math.floor(Math.random() * 100000)
        ).padStart(5, "0")}`;
      }
      usedCodes.add(code);
      return code;
    };

    /* -------------------------------------------------
     * STEP 3: Create / update assets
     * ------------------------------------------------- */
    Object.values(grouped).forEach((assetGroup, index) => {
      let assetCode = assetGroup.assetCode;

      // Generate assetCode if missing
      if (!assetCode) {
        assetCode = generateAssetCode(newAssetCodeList.length);
      }

      const existingAssetId =
        newAssetCodeList.find((a) => a.assetCode === assetCode)?.assetId ||
        null;

      const existingAsset =
        existingAssetId !== null
          ? newData.find((a) => a.assetId === existingAssetId)
          : null;

      if (!existingAsset) {
        /* ---------- Create new asset ---------- */
        const newAsset = assetDefaults(
          {
            assetName: assetGroup.assetName,
            cost: assetGroup.cost,
            caType: assetGroup.caType,
            noOfYears: assetGroup.noOfYears,
            initialAllowanceRate: assetGroup.initialAllowanceRate,
            principalPaid: assetGroup.principalPaid,
          },
          globalDeferredYears,
          categories
        );

        newAssetCodeList.push({
          assetId: newAsset.assetId,
          assetCode,
        });

        newData.push(newAsset);
      } else {
        /* ---------- Update existing asset ---------- */
        assetGroup.principalPaid.forEach((p) => {
          const existingPayment = existingAsset.principalPaid.find(
            (ep) => ep.year === p.year
          );

          if (existingPayment) {
            existingPayment.amount = p.amount;
          } else {
            existingAsset.principalPaid.push(p);
          }
        });

        existingAsset.assetName = assetGroup.assetName;
        existingAsset.cost = assetGroup.cost;
        existingAsset.caType = assetGroup.caType;
        existingAsset.noOfYears = assetGroup.noOfYears;
      }
    });

    /* -------------------------------------------------
     * STEP 4: Recalculate and persist
     * ------------------------------------------------- */
    const result = processAssetsWithPrincipalDetails(
      caSettings,
      newData,
      yearOfAssessment
    );

    setAssets(result.assets);
    setOverAllPrincipalDetails(result.overallPrincipalDetails);
    setAssetCodeList(newAssetCodeList);

    setHpToolsLoading(false);
    setSelectedTabIndex(result.assets.length - 1);
    setSelectedItem(result.assets[result.assets.length - 1]);
    handleCloseHpToolDialog();
  };

  const renderMainContent = () => (
    <Box>
      {unavailableSourceAssets.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 2,
            borderRadius: 2,
            bgcolor: (theme) => alpha(theme.palette.warning.main, 0.075),
            border: "1px solid",
            borderColor: (theme) => alpha(theme.palette.warning.main, 0.16),
          }}
        >
          <Stack direction='row' spacing={1.5} alignItems='center'>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: (theme) => alpha(theme.palette.warning.main, 0.12),
                color: "warning.main",
                flexShrink: 0,
              }}
            >
              <WarningAmberIcon fontSize='small' />
            </Box>
            <Box>
              <Typography variant='h6' fontWeight={600} color='warning.dark'>
                Source unavailable
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                {unavailableSourceAssets.length} imported{" "}
                {unavailableSourceAssets.length === 1
                  ? "asset is"
                  : "assets are"}{" "}
                no longer available in the source schedule.
              </Typography>
            </Box>
          </Stack>

          <Divider sx={{ my: 2.5 }} />

          <Stack spacing={1.5}>
            {unavailableSourceAssets.map(({ asset, index }) => (
              <Paper
                key={asset.assetId}
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: "background.paper",
                  borderRadius: 2,
                  boxShadow: "none",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  alignItems={{ xs: "flex-start", md: "center" }}
                  justifyContent='space-between'
                  spacing={2}
                >
                  <Box>
                    <Typography variant='body1' fontWeight={500}>
                      {asset.assetName || `Asset ${index + 1}`}
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      Source: {asset.source || "Imported source"}
                      {asset.sourceSchedule ? ` (${asset.sourceSchedule})` : ""}
                    </Typography>
                  </Box>

                  <Stack direction='row' spacing={1} flexWrap='wrap'>
                    <Button
                      size='small'
                      variant='outlined'
                      color='warning'
                      onClick={() => handleMarkAssetIndependent(asset.assetId)}
                      sx={{
                        textTransform: "none",
                        borderRadius: 2,
                      }}
                    >
                      Keep
                    </Button>
                    <Button
                      size='small'
                      variant='outlined'
                      color='error'
                      onClick={() => handleDeleteAsset(index)}
                      sx={{
                        textTransform: "none",
                        borderRadius: 2,
                        bgcolor: (theme) =>
                          alpha(theme.palette.error.main, 0.04),
                        borderColor: (theme) =>
                          alpha(theme.palette.error.main, 0.8),
                        color: (theme) => alpha(theme.palette.error.main, 0.9),
                        "&:hover": {
                          bgcolor: (theme) =>
                            alpha(theme.palette.error.main, 0.08),
                          borderColor: (theme) =>
                            alpha(theme.palette.error.main, 0.9),
                          color: (theme) => alpha(theme.palette.error.main, 1),
                        },
                      }}
                    >
                      Remove
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Paper>
      )}

      <Grid container spacing={3}>
        {/* Asset List & Details - Integrated Design */}
        <Grid item xs={12}>
          {viewMode === "tabs" ? (
            <Paper
              elevation={0}
              sx={{
                display: "flex",
                flexDirection: "row",
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                overflow: "hidden",
                bgcolor: (theme) => alpha(theme.palette.background.paper, 0.98),
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                minHeight: 300,
              }}
            >
              {/* Compact Tab List */}
              <Box
                sx={{
                  minWidth: 110,
                  maxWidth: 140,
                  borderRight: "1px solid",
                  borderColor: "divider",
                  bgcolor: "transparent",
                  py: 1,
                  px: 0.5,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "stretch",
                  height: "88vh",
                  overflow: "hidden",
                }}
              >
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={assets.map((asset) => asset.assetId)}
                    strategy={verticalListSortingStrategy}
                  >
                    <Tabs
                      orientation='vertical'
                      value={
                        selectedTabIndex !== null ? selectedTabIndex : false
                      }
                      sx={{
                        minWidth: 0,
                        width: "100%",
                        height: "100%",
                        overflowY: "auto",
                        ".MuiTab-root": {
                          minHeight: 48,
                          fontSize: 15,
                          px: 2,
                          py: 1,
                          borderRadius: 2,
                          mx: 0,
                          my: 1,
                        },
                      }}
                      variant='scrollable'
                      TabIndicatorProps={{
                        style: {
                          left: 0,
                          right: "auto",
                          width: 3,
                          borderRadius: 2,
                        },
                      }}
                    >
                      {assets &&
                        assets.map((item, idx) => (
                          <DraggableTab
                            key={item.assetId}
                            asset={item}
                            index={idx}
                            isSelected={selectedTabIndex === idx}
                            onSelect={(idx) => {
                              setSelectedTabIndex(idx);
                              setSelectedItem(assets[idx]);
                            }}
                          />
                        ))}
                      {canEditSchedule && (
                        <AddNewTab onAddNew={handleAddAsset} />
                      )}
                    </Tabs>
                  </SortableContext>
                </DndContext>
              </Box>
              {/* Details Table - now visually integrated */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: isSmallScreen ? 1 : 3,
                    height: isSmallScreen ? "auto" : "88vh",
                    overflowY: "auto",
                    borderRadius: 0,
                    border: "none",
                    bgcolor: "transparent",
                    boxShadow: "none",
                  }}
                >
                  {selectedItem ? (
                    <Stack spacing={3}>
                      {/* Asset Info */}
                      <AssetInfo
                        assetCodeList={assetCodeList}
                        selectedItem={selectedItem}
                        onEditAsset={handleEditAsset}
                        onViewPrincipalPaid={() => setViewPrincipalPaid(true)}
                        onDeleteAsset={() =>
                          handleDeleteAsset(selectedTabIndex)
                        }
                        isSmallScreen={isSmallScreen}
                        canEdit={canEditSchedule}
                      />

                      {/* Asset Status & Deferral Panel */}
                      {canEditSchedule && (
                        <AssetStatusPanel
                          selectedItem={selectedItem}
                          yearOfAssessment={yearOfAssessment}
                          deferredYears={selectedItem?.deferredYears || []}
                          onDeferralChange={handleDeferralChange}
                          disposed={selectedItem.isCurrentYearDisposed}
                          onDisposedToggle={() => {
                            if (
                              !selectedItem.isCurrentYearDisposed &&
                              !selectedItem.categoryId
                            ) {
                              handleError(
                                "You must define the category before disposing of it."
                              );
                              return;
                            }
                            const updated = {
                              ...selectedItem,
                              isCurrentYearDisposed:
                                !selectedItem.isCurrentYearDisposed,
                              deferredYears: !selectedItem.isCurrentYearDisposed
                                ? (selectedItem.deferredYears || []).filter(
                                    (y) => y < yearOfAssessment
                                  )
                                : selectedItem.deferredYears || [],
                            };
                            let updatedAssets = [...assets];
                            updatedAssets[selectedTabIndex] = updated;
                            const result = processAssetsWithPrincipalDetails(
                              caSettings,
                              updatedAssets,
                              yearOfAssessment
                            );

                            setAssets(result.assets);
                            setSelectedItem(result.assets[selectedTabIndex]);
                            setOverAllPrincipalDetails(
                              result.overallPrincipalDetails
                            );
                          }}
                        />
                      )}
                      <PrincipalPaidTable
                        selectedItem={selectedItem}
                        deferredYears={selectedItem?.deferredYears || []}
                        yearOfAssessment={yearOfAssessment}
                        showDecimals={false}
                        onShowDecimalsChange={setShowDecimals}
                      />
                    </Stack>
                  ) : (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 4,
                        textAlign: "center",
                        bgcolor: (theme) =>
                          alpha(theme.palette.primary.main, 0.02),
                        border: "1px dashed",
                        borderColor: (theme) =>
                          alpha(theme.palette.primary.main, 0.2),
                        borderRadius: 2,
                      }}
                    >
                      <ReceiptIcon
                        sx={{
                          fontSize: 48,
                          color: (theme) =>
                            alpha(theme.palette.primary.main, 0.3),
                          mb: 2,
                        }}
                      />
                      <Typography
                        variant='body1'
                        color='text.secondary'
                        gutterBottom
                      >
                        Select an asset to view the details
                      </Typography>
                    </Paper>
                  )}
                </Paper>
              </Box>
            </Paper>
          ) : (
            <AssetListView
              assets={assets}
              importableAssets={importableAssets}
              onImport={() => setImportAssetsDialogOpen(true)}
              isFullscreen={fullscreenOpen}
              onEditAsset={(assetId) => {
                const foundIndex = assets.findIndex(
                  (a) => a.assetId === assetId
                );
                if (foundIndex !== -1) {
                  setSelectedTabIndex(foundIndex);
                  setSelectedItem(assets[foundIndex]);
                  setEditAsset(true);
                  setIsCreatingNew(false);
                }
              }}
              onDeleteAsset={handleDeleteAsset}
              onViewAsset={(assetId) => {
                const idx = assets.findIndex((a) => a.assetId === assetId);

                if (idx !== -1) {
                  setSelectedTabIndex(idx);
                  setSelectedItem(assets[idx]);
                  setViewMode("tabs");
                }
              }}
              onAddAsset={handleAddAsset}
              onDuplicateAsset={handleDuplicateAsset}
              onDefinePrincipalPaid={(assetId) => {
                const index = assets.findIndex((a) => a.assetId === assetId);
                if (index === -1) return;
                setSelectedTabIndex(index);
                setSelectedItem(assets[index]);
                setViewPrincipalPaid(true);
              }}
              selectedAssetId={selectedItem?.assetId || null}
              onSelectAsset={(assetId) => {
                const index = assets.findIndex((a) => a.assetId === assetId);
                if (index === -1) return;
                setSelectedTabIndex(index);
                setSelectedItem(assets[index]);
              }}
              onBulkDeleteAssets={handleBulkDeleteAssets}
              onBulkDisposeAssets={handleBulkDisposeAssets}
            />
          )}
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <Box sx={{ padding: 4 }}>
      <ScheduleHeader
        {...{
          title: schedule.type,
          companyName,
          uen,
          yearOfAssessment,
          financialYearStart,
          financialYearEnd,
          handleSave,
          showSettings: true,
          hasActiveSettings: globalDeferredYears.length > 0,
          onSettingsClick: () => setSettingsDialogOpen(true),
          handleExcel: handleOpenExcelUploadDialog,
          handleTool: handleOpenHpToolDialog,
          toolName: "HP Calculator",
          deleteSchedule,
          canEditSchedule,
        }}
      >
        <PdfGenerator
          schedule={schedule}
          company={{
            name: companyName,
            uen: uen,
            yearOfAssessment: yearOfAssessment,
            financialYearStart: financialYearStart,
            financialYearEnd: financialYearEnd,
          }}
          assets={assets}
          assetCodeList={assetCodeList}
          summaryData={summaryData}
          caTypeTotals={caTypeTotals}
          notes={notes}
          reviewNotes={reviewNotes}
          showDecimals={false}
        />
      </ScheduleHeader>
      <br />

      {/* View Mode Toggle */}
      {canEditSchedule && (
        <ViewModeToggle
          value={viewMode}
          onChange={(value) => {
            if (canEditSchedule) {
              setViewMode(value);
            }
          }}
          options={[
            { value: "tabs", label: "Tab View", icon: <ViewModuleIcon /> },
            { value: "list", label: "List View", icon: <ViewListIcon /> },
          ]}
          onFullscreenOpen={() => setFullscreenOpen(true)}
        />
      )}

      {/* Main Content */}
      {!fullscreenOpen && renderMainContent()}

      {/* Summary Table */}
      <Box sx={{ mt: 4 }}>
        <HirePurchaseSummary
          summaryData={summaryData}
          caTypeTotals={caTypeTotals}
          yearOfAssessment={yearOfAssessment}
        />
      </Box>

      {/* Asset Info Dialog - Available in both view modes */}
      <AssetInfoDialog
        open={editAsset}
        onClose={handleCloseAssetInfo}
        categoryOption={categoryOption}
        onImport={() => {
          handleCloseAssetInfo();
          setImportAssetsDialogOpen(true);
        }}
        importableAssets={importableAssets}
        assetData={selectedItem}
        caSettings={caSettings}
        caTypeOptions={caTypeOptions}
        onSave={handleAssetSave}
        isCreatingNew={isCreatingNew}
      />

      <PrincipalPaidDialog
        open={viewPrincipalPaid}
        onClose={(principalPaid) => {
          setViewPrincipalPaid(false);

          if (!principalPaid) {
            return;
          }

          if (!selectedItem || selectedTabIndex === null) {
            return;
          }

          selectedItem.principalPaid = principalPaid;

          let updatedRows = [...assets];
          updatedRows[selectedTabIndex].principalPaid = principalPaid;

          const result = processAssetsWithPrincipalDetails(
            caSettings,
            updatedRows,
            yearOfAssessment
          );
          setAssets(result.assets);
          setOverAllPrincipalDetails(result.overallPrincipalDetails);
          setSelectedItem(result.assets[selectedTabIndex]);
        }}
        selectedItem={selectedItem}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteAssetDialog
        open={deleteConfirmOpen}
        onClose={cancelDeleteAsset}
        onConfirm={confirmDeleteAsset}
        assetName={assetToDelete?.asset?.assetName || "Unknown Asset"}
      />

      <Divider sx={{ marginY: 4 }} />
      <ScheduleNotes
        notes={notes}
        setNotes={setNotes}
        reviewNotes={reviewNotes}
        setReviewNotes={setReviewNotes}
        enableEdit={canEditSchedule}
      />

      {/* Settings Dialog */}
      <HirePurchaseSettingsDialog
        open={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
        globalDeferredYears={globalDeferredYears}
        onSave={handleSettingsSave}
        yearOfAssessment={yearOfAssessment}
        existingAssets={assets}
        codePrefix={codePrefix}
        assetCodeList={assetCodeList}
      />
      <FullscreenDialog
        open={fullscreenOpen}
        onClose={() => setFullscreenOpen(false)}
        title={schedule.type}
        subtitle={`${companyName} (${uen}) - YA ${yearOfAssessment}`}
        showAddButton
        addButtonLabel='Add Asset'
        onAdd={handleAddAsset}
        addButtonInHeader
      >
        {renderMainContent()}
      </FullscreenDialog>

      <AssetImportManager
        open={importAssetsDialogOpen}
        onClose={() => setImportAssetsDialogOpen(false)}
        categories={categories}
        allAssets={allImportTypeAssets}
        availableAssets={importableAssets}
        importedAssets={importedAssets}
        onImportSelected={onImportFromAdditions}
      />

      <HirePurchaseExcelUpload
        open={excelDialogOpen}
        handleCloseDialog={handleCloseExcelUploadDialog}
        assets={assets}
        assetCodeList={assetCodeList}
        onExcelAddRows={onExcelAddRows}
        caTypeOptions={caTypeOptions}
      />

      {/* HP Tool Dialog */}
      <HirePurchaseToolDialog
        hpToolsLoading={hpToolsLoading}
        open={hpToolDialogOpen}
        caSettings={caSettings}
        caTypeOptions={caTypeOptions}
        onClose={handleCloseHpToolDialog}
        onAddToHPSchedule={handleAddToHPSchedule}
      />
    </Box>
  );
};

export default HirePurchaseSchedule;
